/**
 * KitchenDisplayLiveSync
 *
 * Modeled directly on src/renderer/components/sales/RunningOrderLiveSync.tsx:
 * Supabase Realtime gives a fast "something changed, refetch" push on top of
 * PowerSync's own local-SQLite sync (which usePowerSyncChange also listens
 * for, covering tickets that arrive via background sync rather than a live
 * Realtime event — e.g. this device was briefly offline when the event fired).
 *
 * A brand new 'NEW' ticket in the currently selected department plays a
 * sound (unless muted); any other change just triggers a silent refresh.
 */

import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { supabase } from '@/renderer/services/supabase';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectOutletId } from '@store/auth/authSelectors';
import {
  fetchKdsBoard,
  selectKdsIsMuted,
  selectKdsSelectedDepartmentId,
} from '@store/kds';
import { playKdsNewTicketSound } from '@/renderer/util/kdsSound';
import React, { useEffect } from 'react';

export const KitchenDisplayLiveSync: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);
  const selectedDepartmentId = useAppSelector(selectKdsSelectedDepartmentId);
  const isMuted = useAppSelector(selectKdsIsMuted);

  const refresh = (): void => {
    if (selectedDepartmentId) {
      dispatch(fetchKdsBoard(selectedDepartmentId));
    }
  };

  // Covers tickets that arrive via background PowerSync sync (e.g. this
  // device was briefly offline when the originating Realtime event fired).
  usePowerSyncChange(['kitchen_tickets'], () => {
    refresh();
  });

  useEffect(() => {
    if (!outletId) return undefined;

    let disposed = false;
    let refreshTimeout: number | null = null;

    const queueRefresh = (playSound: boolean) => {
      if (disposed) return;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      refreshTimeout = window.setTimeout(() => {
        if (playSound && !isMuted) {
          playKdsNewTicketSound();
        }
        refresh();
      }, 150);
    };

    const channel = supabase
      .channel(`kitchen-display-${outletId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'kitchen_tickets',
          filter: `outlet_id=eq.${outletId}`,
        },
        (payload) => {
          const departmentId = (payload.new as { department_id?: string })
            ?.department_id;
          queueRefresh(departmentId === selectedDepartmentId);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'kitchen_tickets',
          filter: `outlet_id=eq.${outletId}`,
        },
        () => queueRefresh(false),
      )
      .subscribe();

    return () => {
      disposed = true;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletId, selectedDepartmentId, isMuted]);

  return null;
};
